import { useTranslation } from 'react-i18next'
import { ArrowUpRight, MapPin, Globe2 } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { LocalizedLink } from '@/components/LocalizedLink'
import { usePageSEO } from '@/hooks/usePageSEO'
import {
  partnerCountries,
  partnerCountryKey,
  partnerEntryKey,
  type PartnerCountry,
  type PartnerEntry,
} from '@/data/partners'
import { cn } from '@/lib/utils'

/* ============================================================
   CountryFlagChip — typographic "flag" built from the ISO code.
   Industrial, disciplined, keeps brand blue as the only accent.
   ============================================================ */
function CountryFlagChip({ code }: { code: PartnerCountry['code'] }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'relative inline-flex h-14 w-14 items-center justify-center',
        'bg-ink-900 text-white',
        'shadow-[inset_0_-3px_0_0_var(--color-brand-500)]'
      )}
    >
      {/* Corner ticks — give the chip a machined feel */}
      <span className="absolute left-1 top-1 block h-2 w-px bg-brand-500" />
      <span className="absolute left-1 top-1 block h-px w-2 bg-brand-500" />
      <span className="absolute right-1 bottom-1 block h-2 w-px bg-brand-500" />
      <span className="absolute right-1 bottom-1 block h-px w-2 bg-brand-500" />
      <span className="font-display font-bold tracking-[0.08em] text-lg tabular-nums">
        {code}
      </span>
    </div>
  )
}

/* ============================================================
   PartnerRow — a single "technical record" entry line.
   Renders a bold partner name, small-caps role, city, and an
   arrow link to the external website when available.
   ============================================================ */
function PartnerRow({
  entry,
  index,
  total,
}: {
  entry: PartnerEntry
  index: number
  total: number
}) {
  const { t } = useTranslation()
  const role = entry.hasMeta ? t(partnerEntryKey(entry, 'role')) : undefined
  const city = entry.hasMeta ? t(partnerEntryKey(entry, 'city')) : undefined
  const description = entry.hasMeta
    ? t(partnerEntryKey(entry, 'description'))
    : undefined

  const isLast = index === total - 1

  const content = (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:items-center">
      {/* Logo frame — disciplined white tile with ink border and brand tick */}
      <div className="md:col-span-3">
        <div
          className={cn(
            'relative flex h-20 md:h-24 w-full items-center justify-center',
            'bg-white border border-ink-200',
            'px-5 py-3'
          )}
        >
          <span
            aria-hidden="true"
            className="absolute left-0 top-0 h-[2px] w-8 bg-brand-500"
          />
          {entry.logo ? (
            <img
              src={entry.logo}
              alt={t('partners.logoAlt', { name: entry.name })}
              loading="lazy"
              decoding="async"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="font-display font-bold text-lg text-ink-400 tracking-[0.12em]">
              {entry.name}
            </span>
          )}
        </div>
      </div>

      {/* Middle-left: index + name + role */}
      <div className="md:col-span-4">
        <div className="flex items-baseline gap-3">
          <span className="font-display font-bold tabular-nums text-xs text-brand-600 tracking-[0.18em]">
            {String(index + 1).padStart(2, '0')}
          </span>
          <h4 className="font-display font-bold leading-tight text-ink-900 text-lg sm:text-xl">
            {entry.name}
          </h4>
        </div>
        {role && (
          <p className="mt-2 pl-7 text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500">
            {role}
          </p>
        )}
      </div>

      {/* Middle-right: description + city */}
      <div className="md:col-span-3 md:pl-4 md:border-l md:border-ink-200">
        {description && (
          <p className="text-sm leading-relaxed text-ink-600 fx-text-pretty">
            {description}
          </p>
        )}
        {city && (
          <p className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-ink-500">
            <MapPin
              className="h-3.5 w-3.5 text-brand-500 shrink-0"
              strokeWidth={2.5}
              aria-hidden="true"
            />
            <span>{city}</span>
          </p>
        )}
      </div>

      {/* Right: website link */}
      <div className="md:col-span-2 md:text-right">
        {entry.website && (
          <a
            href={entry.website}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('partners.visitWebsiteAria', { name: entry.name })}
            className={cn(
              'group/link inline-flex items-center gap-2',
              'text-xs font-bold uppercase tracking-[0.16em]',
              'text-brand-600 hover:text-brand-700',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2'
            )}
          >
            {t('partners.visitWebsite')}
            <ArrowUpRight
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
              strokeWidth={2.75}
            />
          </a>
        )}
      </div>
    </div>
  )

  return (
    <li
      className={cn(
        'relative px-5 py-6 md:px-7 md:py-7',
        !isLast && 'border-b border-ink-200'
      )}
    >
      {/* Left brand bar — only on hover-capable entries, subtle always */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-6 bottom-6 w-[2px] bg-brand-500/0 transition-colors duration-200"
      />
      {content}
    </li>
  )
}

/* ============================================================
   CountryBlock — the main card for each country. Holds the
   flag chip, country name, entry count, and the list of partners.
   ============================================================ */
function CountryBlock({
  country,
  position,
}: {
  country: PartnerCountry
  position: number
}) {
  const { t } = useTranslation()
  const countryName = t(partnerCountryKey(country))
  const count = country.entries.length
  const countLabel =
    count === 1
      ? t('partners.countryEntryCountSingular')
      : t('partners.countryEntryCount')

  return (
    <article
      aria-labelledby={`country-${country.code}-title`}
      className="relative isolate overflow-hidden bg-white border border-ink-200 shadow-sm"
    >
      {/* Top brand rule — unifies the card family */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] bg-brand-500"
      />

      {/* Position badge — tiny coordinate marker at top-right */}
      <span
        aria-hidden="true"
        className="absolute top-4 right-5 font-display font-bold text-[10px] tracking-[0.22em] uppercase text-ink-400"
      >
        N° {String(position).padStart(2, '0')}
      </span>

      {/* Card header */}
      <header className="px-5 pt-8 pb-6 md:px-7 md:pt-9 md:pb-7 border-b border-ink-200 bg-gradient-to-br from-white via-white to-ink-50">
        <div className="flex items-start gap-4 md:gap-5">
          <CountryFlagChip code={country.code} />
          <div className="flex-1 min-w-0">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.26em] text-brand-600">
              {t('partners.countryEyebrow')}
            </p>
            <h3
              id={`country-${country.code}-title`}
              className="font-display font-bold text-2xl md:text-[1.75rem] leading-tight text-ink-900"
            >
              {countryName}
            </h3>
            <p className="mt-2 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.14em] text-ink-500 uppercase">
              <span
                aria-hidden="true"
                className="block h-[2px] w-5 bg-brand-500"
              />
              <span className="font-display font-bold tabular-nums text-ink-900">
                {String(count).padStart(2, '0')}
              </span>
              <span>{countLabel}</span>
            </p>
          </div>
        </div>
      </header>

      {/* Partner list or empty state */}
      {country.entries.length > 0 ? (
        <ul className="relative">
          {country.entries.map((entry, i) => (
            <PartnerRow
              key={entry.key}
              entry={entry}
              index={i}
              total={country.entries.length}
            />
          ))}
        </ul>
      ) : (
        <div className="px-5 py-8 md:px-7 md:py-10 text-sm text-ink-500 italic">
          {t('partners.emptyCountry')}
        </div>
      )}
    </article>
  )
}

/* ============================================================
   NetworkStats — three small engineered stats under the intro.
   ============================================================ */
function NetworkStats() {
  const { t } = useTranslation()
  const stats = [
    {
      label: t('partners.networkStat1Label'),
      value: t('partners.networkStat1Value'),
    },
    {
      label: t('partners.networkStat2Label'),
      value: t('partners.networkStat2Value'),
    },
    {
      label: t('partners.networkStat3Label'),
      value: t('partners.networkStat3Value'),
    },
  ]

  return (
    <dl className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-ink-200 border border-ink-200">
      {stats.map((s) => (
        <div
          key={s.label}
          className="relative bg-white px-5 py-5 md:px-6 md:py-6"
        >
          <span
            aria-hidden="true"
            className="absolute top-0 left-0 h-[2px] w-10 bg-brand-500"
          />
          <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
            {s.label}
          </dt>
          <dd className="mt-2 font-display font-bold text-2xl md:text-[1.75rem] leading-none text-ink-900 tracking-tight">
            {s.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/* ============================================================
   JoinNetworkRibbon — editorial CTA at the end of the page.
   This is a page-local ribbon, distinct from the global FinalCTA.
   ============================================================ */
function JoinNetworkRibbon() {
  const { t } = useTranslation()

  const points = [
    t('partners.join.point1'),
    t('partners.join.point2'),
    t('partners.join.point3'),
  ]

  return (
    <section
      aria-labelledby="partners-join-title"
      className="relative isolate overflow-hidden bg-white border border-ink-200 shadow-sm"
    >
      {/* Left brand bar */}
      <span
        aria-hidden="true"
        className="absolute top-0 bottom-0 left-0 w-[4px] bg-brand-500"
      />
      {/* Diagonal accent bleeding in from the right */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-1/3 opacity-[0.07] fx-diagonal-accent"
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-7 md:p-10 lg:p-12 items-center">
        <div className="lg:col-span-7">
          <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.26em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('partners.join.eyebrow')}
          </p>

          <h2
            id="partners-join-title"
            className="fx-text-balance font-display font-bold leading-[1.1] text-ink-900 text-2xl sm:text-3xl md:text-[2rem]"
          >
            {t('partners.join.title')}
          </h2>

          <p className="mt-5 max-w-2xl fx-text-pretty text-base md:text-[17px] leading-relaxed text-ink-600">
            {t('partners.join.body')}
          </p>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {points.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm text-ink-700"
              >
                <span
                  aria-hidden="true"
                  className="mt-2 block h-[2px] w-4 bg-brand-500 shrink-0"
                />
                <span className="leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5 flex lg:justify-end">
          <LocalizedLink
            to="contacts"
            className={cn(
              'group/cta relative inline-flex items-center gap-3',
              'rounded-sm bg-brand-500 px-7 py-4',
              'text-sm md:text-base font-bold uppercase tracking-[0.08em] text-white',
              'shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)]',
              'transition-[background-color,transform,box-shadow] duration-200',
              'hover:bg-brand-400 hover:-translate-y-0.5',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
            )}
          >
            <span
              aria-hidden="true"
              className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover/cta:h-8"
            />
            {t('partners.join.cta')}
            <ArrowUpRight
              className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1"
              strokeWidth={2.75}
            />
          </LocalizedLink>
        </div>
      </div>
    </section>
  )
}

/* ============================================================
   Main page
   ============================================================ */
export default function PartnersPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.partners.title'),
    description: t('meta.partners.description'),
    ogTitle: t('meta.partners.ogTitle'),
    ogDescription: t('meta.partners.ogDescription'),
  })

  return (
    <main id="main-content" className="bg-ink-100">
      <PageHeader
        title={t('partners.title')}
        subtitle={t('partners.intro')}
        eyebrow={t('partners.eyebrow')}
      />

      {/* ============================================================
          Network overview — intro + stats
          ============================================================ */}
      <Section tone="muted" spacing="lg">
        <header className="max-w-3xl">
          <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('partners.overviewEyebrow')}
          </p>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl sm:text-3xl md:text-[2rem] flex items-start gap-3">
            <Globe2
              className="h-7 w-7 md:h-8 md:w-8 text-brand-500 shrink-0 mt-1"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span>{t('partners.overviewTitle')}</span>
          </h2>
          <p className="mt-5 fx-text-pretty text-base md:text-lg leading-relaxed text-ink-600">
            {t('partners.networkLead')}
          </p>
        </header>

        <NetworkStats />
      </Section>

      {/* ============================================================
          Country blocks — the core of the page
          ============================================================ */}
      <Section tone="default" spacing="lg">
        <header className="mb-10 md:mb-14 max-w-3xl">
          <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('partners.networkEyebrow')}
          </p>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl sm:text-3xl md:text-[2rem]">
            {t('partners.networkTitle')}
          </h2>
        </header>

        <div className="grid grid-cols-1 gap-6 md:gap-7">
          {partnerCountries.map((country, i) => (
            <CountryBlock
              key={country.code}
              country={country}
              position={i + 1}
            />
          ))}
        </div>
      </Section>

      {/* ============================================================
          Editorial "Join the network" ribbon — page-local CTA
          (distinct from the global FinalCTA mounted in the router)
          ============================================================ */}
      <Section tone="muted" spacing="md">
        <JoinNetworkRibbon />
      </Section>
    </main>
  )
}
